import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Search, UserCheck } from 'lucide-react'
import { formatDateTime } from '@/lib/datetime'
import { formatCurrency, pluralize } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminTripRequest, DriverOption } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { fetchEligibleDrivers } from '@/features/drivers/api'
import { AddOnBadges } from '@/features/trips/AddOnBadges'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { Badge } from '@/components/ui/StatusBadge'
import { EmptyState, ErrorState } from '@/components/ui/States'
import { Skeleton } from '@/components/ui/Skeleton'
import { assignDriver } from './api'
import styles from './AssignDriverDialog.module.css'

interface AssignDriverDialogProps {
  request: AdminTripRequest | null
  onClose: () => void
}

/**
 * Approves a trip request by putting a driver on it.
 *
 * Only eligible drivers are offered — enabled accounts with the DRIVER role.
 * The legacy dashboard listed every driver except a hard-coded id 19, which
 * meant suspended drivers were assignable and nobody could say why 19 was not.
 */
export function AssignDriverDialog({ request, onClose }: AssignDriverDialogProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [search, setSearch] = useState('')

  const open = request !== null

  const driversQuery = useQuery({
    queryKey: queryKeys.drivers.eligible,
    queryFn: fetchEligibleDrivers,
    enabled: open,
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!open) {
      setSelectedId(null)
      setSearch('')
    }
  }, [open])

  const assignMutation = useAdminMutation({
    mutationFn: ({ requestId, driverId }: { requestId: number; driverId: number }) =>
      assignDriver(requestId, driverId),
    successTitle: 'Trip request approved',
    // Approving creates a trip and empties a slot in the queue, so the trips
    // list and the overview counters are both out of date.
    invalidate: [queryKeys.tripRequests.all, queryKeys.trips.all, queryKeys.drivers.all, queryKeys.dashboard.all],
    errorTitle: 'Could not assign the driver',
    onSuccess: onClose,
  })

  // The server's own verdict on whether this booking's payment allows a driver,
  // and the sentence it would refuse with. Read rather than re-derived: working
  // it out here from a payment-status string is exactly how a dashboard ends up
  // offering an action the API then rejects with an opaque 409.
  const blockedReason = request?.assignmentBlockedReason ?? null

  const term = search.trim().toLowerCase()
  const drivers = (driversQuery.data ?? []).filter((driver) =>
    term === ''
      ? true
      : (driver.name ?? '').toLowerCase().includes(term) || (driver.phone ?? '').toLowerCase().includes(term),
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Approve trip request"
      description="Assigning a driver confirms the request and creates the trip."
      size="lg"
      busy={assignMutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={assignMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            iconLeft={<UserCheck aria-hidden="true" />}
            disabled={selectedId === null || blockedReason !== null}
            loading={assignMutation.isPending}
            onClick={() =>
              request && selectedId !== null
                ? assignMutation.mutate({ requestId: request.id, driverId: selectedId })
                : undefined
            }
          >
            Assign driver &amp; confirm
          </Button>
        </>
      }
    >
      {request ? (
        <>
          {/*
            Raised above the booking summary rather than shown as a disabled
            button with no explanation. Each reason has a different fix — verify
            the transfer, attach a missing receipt, switch a dead card payment to
            Instapay, or leave a refunded booking alone — and an operator who is
            only told "no" has nowhere to go. The Payment action on the row is
            where all of those are done, so the notice says so.
          */}
          {blockedReason ? (
            <div className={styles.blocked} role="alert">
              <AlertTriangle aria-hidden="true" />
              <div>
                <strong>This booking cannot be assigned a driver yet</strong>
                <p>{blockedReason}</p>
                <p className={styles.blockedHint}>
                  Close this and use the <strong>Payment</strong> action on the row to verify the
                  transfer, attach the proof of payment, or switch the payment method.
                </p>
              </div>
            </div>
          ) : null}

          <section className={styles.summary}>
            <KeyValueGrid>
              <KeyValue label="Reference">
                <span className={styles.reference}>{request.bookingReference ?? `#${request.id}`}</span>
              </KeyValue>
              <KeyValue label="Passenger">
                {request.userName ?? '—'}
                {request.userPhone ? <span className={styles.muted}> · {request.userPhone}</span> : null}
              </KeyValue>
              <KeyValue label="Package">{request.packageName ?? '—'}</KeyValue>
              {/*
                Shown before a driver is chosen because it changes who can take
                the job: a return leg with hours at the coast is a full day out,
                not a one-way drive. Priced so the fare below adds up.
              */}
              {request.addOns.any ? (
                <KeyValue label="Add-ons">
                  <AddOnBadges addOns={request.addOns} priced />
                </KeyValue>
              ) : null}
              {/*
                The car is here rather than further down because this dialog is
                the moment a driver is chosen, and the driver has to find that
                car at the pickup. Bookings taken before the app asked for it
                read 'Standard'.
              */}
              <KeyValue label="Car">{request.carModel ?? '—'}</KeyValue>
              <KeyValue label="Scheduled start">{formatDateTime(request.startTime)}</KeyValue>
              <KeyValue label="Fare">{formatCurrency(request.basePrice)}</KeyValue>
              <KeyValue label="Payment">
                {request.paymentMethod ?? '—'}
                {request.paymentStatus ? <span className={styles.muted}> · {request.paymentStatus}</span> : null}
              </KeyValue>
              <KeyValue label="Pickup" wide>
                {request.pickupAddress ?? '—'}
              </KeyValue>
              <KeyValue label="Drop-off" wide>
                {request.dropoffAddress ?? '—'}
              </KeyValue>
            </KeyValueGrid>
          </section>

          <section className={styles.picker}>
            <div className={styles.pickerHeader}>
              <h3 className={styles.pickerTitle}>Choose a driver</h3>
              <p className={styles.pickerHint}>
                Only active driver accounts are listed. Workload counts trips that are confirmed, en route or running.
              </p>
            </div>

            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter drivers by name or phone…"
              aria-label="Filter drivers"
              iconLeft={<Search aria-hidden="true" />}
              disabled={assignMutation.isPending}
            />

            {driversQuery.isLoading ? (
              <div className={styles.list}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} height={56} radius="var(--radius-md)" />
                ))}
              </div>
            ) : driversQuery.isError ? (
              <ErrorState error={driversQuery.error} onRetry={() => void driversQuery.refetch()} compact />
            ) : drivers.length === 0 ? (
              <EmptyState
                title={term ? 'No drivers match that search' : 'No drivers are available'}
                description={
                  term
                    ? 'Clear the filter to see every active driver.'
                    : 'Every driver account is suspended, or none has been created yet. Add or reactivate a driver first.'
                }
                compact
              />
            ) : (
              <ul className={styles.list} role="radiogroup" aria-label="Available drivers">
                {drivers.map((driver: DriverOption) => {
                  const selected = selectedId === driver.id
                  return (
                    <li key={driver.id}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={selected ? `${styles.driver} ${styles.driverSelected}` : styles.driver}
                        onClick={() => setSelectedId(driver.id)}
                        disabled={assignMutation.isPending}
                      >
                        <Avatar name={driver.name} src={driver.profileImageUrl} size="sm" />
                        <span className={styles.driverText}>
                          <span className={styles.driverName}>{driver.name ?? `Driver #${driver.id}`}</span>
                          <span className={styles.driverPhone}>{driver.phone ?? 'No phone number'}</span>
                        </span>
                        {driver.activeTripCount > 0 ? (
                          <Badge tone="warning">
                            {driver.activeTripCount} {pluralize(driver.activeTripCount, 'trip')}
                          </Badge>
                        ) : (
                          <Badge tone="success">Free</Badge>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </Dialog>
  )
}
